import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLockedBody } from "usehooks-ts";
import { toast } from "react-toastify";

import { Layout } from "@/layout/layout";
import { Loader } from "@ui/loader/loader";
import { VioletButton } from "@ui/violet-button/violet-button";

import { ListItem } from "@/pages/confirmation/components/list-item";
import { TokenItem } from "@/pages/confirmation/components/token-item";

import { Icon } from "@ui/icons/icon";
import { ICON_COLLECTION } from "@ui/icons/icon-list";

import { CreateFundFormType } from "@/pages/create-fund/types";
import { createFund } from "@/api/create-fund";
import { appendSignature } from "@/api/append-signature";
import { useCardano } from "@cardano-foundation/cardano-connect-with-wallet";
import { NetworkType } from "@cardano-foundation/cardano-connect-with-wallet-core";
import { CARDANO_NETWORK } from "@constants/config";

// Helper to get Cardanoscan URL based on network
const getCardanoscanUrl = (txHash: string): string => {
  const baseUrl = CARDANO_NETWORK === NetworkType.MAINNET
    ? "https://cardanoscan.io"
    : "https://preprod.cardanoscan.io";
  return `${baseUrl}/transaction/${txHash}`;
};
import { findAddressWithBalance } from "@/helpers/utxo-selection";
import { useBlockchainCooldown } from "@/hooks/useBlockchainCooldown";

export const Confirmation = () => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { enabledWallet } = useCardano({ limitNetwork: CARDANO_NETWORK });
  const { isOnCooldown, remainingSeconds, checkCooldown, startCooldown } = useBlockchainCooldown();

  const { state } = useLocation();
  const navigate = useNavigate();

  // Check cooldown on mount
  useEffect(() => {
    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [checkCooldown]);

  // Prompt wallet "grant access" as soon as the page loads (only if already connected)
  useEffect(() => {
    if (enabledWallet && "cardano" in window) {
      (window as any).cardano[enabledWallet].enable().catch(console.error);
    }
    // Intentionally runs only on mount — avoids interfering with the connect flow
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => {
    if (!state) {
      navigate(-1);
      return;
    }

    navigate("/create", { state });
  };

  useLockedBody(loading);

  if (!state) {
    return (
      <Layout>
        <div className="min-h-screen w-full gap-7 py-8 md:pb-16 px-3 md:px-16 dark:bg-dark-bg">
          <h1 className="text-5xl font-semibold dark:text-white">
            Oops.. Seems like something went wrong.
          </h1>
          <button
            className="dark:text-white hover:text-gray-600 dark:hover:text-gray-600 flex items-center justify-end gap-1 px-6 py-2 rounded-full border border-gray-800 hover:border-gray-500 dark:border-gray-400 dark:hover:border-gray-600 transition-colors duration-300"
            onClick={handleGoBack}
          >
            <Icon iconSize="18px" icon={ICON_COLLECTION.arrowLeft} />
            <span className="text-lg">Go Back</span>
          </button>
        </div>
      </Layout>
    );
  }

  const {
    fundName,
    description,
    creatorName,
    address,
    fee,
    commission,
    hide,
    selectedTokenIds,
    selectedTokens,
  } = state as CreateFundFormType;

  const handleCreateFund = async () => {
    // Check global cooldown period to prevent UTXO contention
    if (!checkCooldown()) {
      toast.error(`Please wait ${remainingSeconds} seconds before creating a fund. This ensures blockchain synchronization.`);
      return;
    }

    setLoading(true);

    try {
      if (!enabledWallet) {
        throw new Error("Wallet not connected");
      }

      if (!("cardano" in window)) {
        throw new Error("Cardano wallet not found");
      }

      const cardano = (window as any).cardano;
      const walletAPI = await cardano[enabledWallet].enable();

      // Step 1: Get UTXOs to find an address with enough funds
      console.log("Getting UTXOs from wallet...");
      const utxosHex = await walletAPI.getUtxos();
      // The backend auto-calculates the ADA needed for the fund UTxO;
      // 10 ADA is a safe estimate for UTXO selection (with 1.5x fee multiplier)
      const requiredLovelace = 10_000_000;
      const userAddress = await findAddressWithBalance(utxosHex, requiredLovelace);
      console.log("Selected address:", userAddress);

      // Step 2: Prepare token data
      const tokens = selectedTokenIds;
      const tokenFactors = selectedTokenIds.map(
        (tokenId) => Math.floor(selectedTokens[tokenId]?.distribution || 0)
      );
      let rest = 100 - tokenFactors.reduce((s, v) => s + v, 0);
      for (let i = 0; rest > 0; i++, rest--) {
        tokenFactors[i] += 1;
      }
      const royaltyFactor = commission ? parseFloat(fee) : 0;

      // Step 3: Call backend to build unsigned transaction
      console.log("Calling backend to build transaction...");
      const unsignedTxCbor = await createFund({
        fundName,
        tokens,
        tokenFactors,
        fundFactor: 420,
        royaltyFactor,
        creatorPkh: null,
        userAddress,
      });
      console.log("Received unsigned TX CBOR, length:", unsignedTxCbor.length);

      // Step 4: Sign with wallet (get witness set only)
      console.log("Requesting wallet signature...");
      const witnessSetCbor = await walletAPI.signTx(unsignedTxCbor, true);
      console.log("Received witness set CBOR, length:", witnessSetCbor.length);

      // Step 5: Send to backend to append signature
      console.log("Sending witness set to backend...");
      const signedTxCbor = await appendSignature(unsignedTxCbor, witnessSetCbor);
      console.log("Received signed TX CBOR, length:", signedTxCbor.length);

      // Step 6: Submit the fully signed transaction
      console.log("Submitting transaction...");
      const txHash = await walletAPI.submitTx(signedTxCbor);
      console.log("Transaction submitted! Hash:", txHash);

      // Show success toast with TX hash link to Cardanoscan
      const TxHashNotification = () => {
        const cardanoscanUrl = getCardanoscanUrl(txHash);

        return (
          <div>
            <div className="font-semibold mb-2">Fund created successfully!</div>
            <a
              href={cardanoscanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:text-blue-100 underline break-all"
              onClick={(e) => {
                // Also copy to clipboard when clicked
                navigator.clipboard.writeText(txHash);
                toast.info("TX hash copied to clipboard!", { autoClose: 2000 });
              }}
            >
              View on Cardanoscan: {txHash.substring(0, 16)}...
            </a>
          </div>
        );
      };

      toast.success(<TxHashNotification />, {
        autoClose: 8000,
        onClick: () => {
          // Open cardanoscan in new tab when toast is clicked
          window.open(getCardanoscanUrl(txHash), '_blank');
        }
      });

      // Start global cooldown to prevent UTXO contention
      startCooldown();

      // Navigate back to home after successful submission
      setTimeout(() => {
        navigate("/");
        toast.info(
          "Newly created funds might take up to 30-60 seconds to appear. Refresh the page if you don't see it yet.",
          { autoClose: 15000 }
        );
      }, 3000);

    } catch (error) {
      console.error("Create fund error:", error);
      toast.error(
        `Failed to create fund: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {loading ? (
        <div className="fixed top-0 bottom-0 left-0 right-0 z-50 bg-dark-bg/70">
          <Loader />
        </div>
      ) : null}
      <div className="min-h-screen w-full flex flex-col gap-7 py-8 md:pb-16 px-3 md:px-16 dark:bg-dark-bg">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          <button
            className="dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-600 flex items-center justify-center md:justify-end gap-1 w-full md:w-fit px-6 py-2 rounded-full border border-gray-800 hover:border-gray-500 dark:border-gray-400 dark:hover:border-gray-600 transition-colors duration-300"
            disabled={loading}
            onClick={handleGoBack}
          >
            <Icon iconSize="24px" icon={ICON_COLLECTION.arrowLeft} />
            <span className="font-sf-pro text-l font-bold">Go Back</span>
          </button>

          <div className="relative group">
            <VioletButton
              text={isOnCooldown ? `Wait ${remainingSeconds}s` : "Create Fund"}
              icon={ICON_COLLECTION.arrowRight}
              disabled={loading || isOnCooldown}
              onClick={handleCreateFund}
              iconClass="white-icon"
              className="w-full md:w-auto px-6 py-2.5"
            />
            {isOnCooldown && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Please wait for blockchain confirmation. Creating funds too quickly may cause errors due to unspent transaction outputs (UTXOs) still being processed.
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6 w-full">
          <div className="h-full w-full md:w-1/2 p-7 rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800">
            <h2 className="font-sf-pro text-2xl font-semibold text-gray-800 dark:text-white">
              Summary
            </h2>
            <ul className="dark:text-white flex flex-col gap-6 mt-8">
              <li>
                <ListItem
                  valueNoWrap
                  label={t("create-fund-form.input-title.name")}
                  value={fundName}
                />
              </li>
              <li>
                <ListItem
                  label={t("create-fund-form.input-title.description")}
                  value={description}
                />
              </li>
              {!hide ? (
                <li>
                  <ListItem
                    valueNoWrap
                    label={t("create-fund-form.input-title.creator")}
                    value={creatorName}
                  />
                </li>
              ) : null}
              {commission ? (
                <>
                  <li>
                    <ListItem
                      label={t("create-fund-form.input-title.address")}
                      value={address}
                    />
                  </li>
                  <li>
                    <ListItem
                      valueNoWrap
                      label={t("create-fund-form.input-title.fee")}
                      value={`${fee} %`}
                    />
                  </li>
                </>
              ) : null}
            </ul>
          </div>
          <div className="h-full w-full md:w-1/2 p-7 rounded-lg bg-white dark:bg-gray-900 border dark:border-gray-800">
            <h2 className="font-sf-pro text-2xl font-semibold text-gray-800 dark:text-white">
              Selected tokens
            </h2>
            <div className="flex items-center justify-between w-full mt-8 py-2 text-s md:text-m font-sf-pro font-medium text-gray-400">
              <span>{t("create-fund-tokens.selected.title.name")}</span>
              <span>Distribution</span>
            </div>
            <ul className="dark:text-white">
              {selectedTokenIds.map((id) => (
                <li
                  className="border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  key={id}
                >
                  <TokenItem token={selectedTokens[id]} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};
